import { getToken as getAppCheckToken } from 'firebase/app-check';
import { httpsCallable } from 'firebase/functions';
import { appCheck, getAppCheck, auth, functions, remoteConfig } from '@/services/firebase';
import { fetchAndActivate, getValue } from 'firebase/remote-config';
import { AppErrorCode, AppException } from '@/shared/types/errors';
import { safeJsonParse } from '@/services/utils/json';
import { assertContentsWithinStreamBudget } from './StreamPayloadGuard';
import { APPROVED_MODELS, INTELLIGENCE_MODELS, getModelKey } from '@/core/config/intelligence-models';
import { RemoteIntelligenceConfigSchema, DEFAULT_REMOTE_CONFIG, RemoteIntelligenceConfig } from './config/RemoteIntelligenceConfig';
import {
    FunctionCallPart,
    GenerateContentResponse,
    WrappedResponse,
    StreamChunk,
    GenerateVideoRequest,
    GenerateSpeechResponse,
    GenerateImageOptions,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    EmbedContentOptions,
    GenerationConfig,
    ContentPart,
    SafetySetting,
    ToolConfig,
    GenerateContentOptions,
    Content,
    Part,
    Schema,
    Tool,
} from '@/shared/types/ai.dto';

import { CircuitBreaker } from './utils/CircuitBreaker';
import { BREAKER_CONFIGS } from './config/breaker-configs';
import { STANDARD_SAFETY_SETTINGS } from './config/safety-settings';
import { InputSanitizer } from './utils/InputSanitizer';
import { TokenUsageService } from './billing/TokenUsageService';
import { logger } from '@/utils/logger';
import { RateLimiter } from './RateLimiter';
import { secureRandomInt } from '@/utils/crypto-random';
import { CostControlService } from '@/services/billing/CostControlService';
import { estimateAgentStreamCost } from './billing/estimateAgentStreamCost';

// Extracted sub-modules
import { isAppCheckError, isAppCheckConfigured } from './appcheck';
import {
    generateText as hlGenerateText,
    generateStructuredData as hlGenerateStructuredData,
    chat as hlChat,
    analyzeImage as hlAnalyzeImage,
    analyzeMultimodal as hlAnalyzeMultimodal,
    generateGroundedContent as hlGenerateGroundedContent,
    captionImage as hlCaptionImage,
    analyzeAudio as hlAnalyzeAudio,
    analyzeFileURI as hlAnalyzeFileURI,
    parseJSON as hlParseJSON,
} from './generators/HighLevelAPI';
import { generateImage as imgGenerate } from './generators/ImageGenerator';
import { generateSpeech as speechGenerate } from './generators/SpeechGenerator';
import {
    embedContent as embeddingEmbed,
    batchEmbedContents as embeddingBatchEmbed,
} from './generators/EmbeddingGenerator';
import { GeminiFileService } from './GeminiFileService';
import type { IntelligenceContext } from './IntelligenceContext';
import type {
    FileDataPart,
    ExtendedGenerativeModel,
    ChatMessage,
} from './types';

type GenerateContentResult = WrappedResponse;
type LiveGenerativeModel = never;

type BackendStreamPayload = {
    text?: string;
    functionCalls?: FunctionCallPart['functionCall'][];
    thoughtSignature?: string;
    complete?: boolean;
    candidates?: Array<{ content?: { parts?: ContentPart[] } }>;
    error?: {
        code?: string;
        message?: string;
        retryable?: boolean;
        category?: string;
        nextActions?: string[];
        retryAfterSeconds?: number;
        providerSubmitted?: boolean;
    };
};

const generationCapacityFromPayload = (
    payload: BackendStreamPayload | null,
): AppException | null => {
    if (payload?.error?.code !== 'GENERATION_CAPACITY_LIMITED') return null;

    const retryAfterSeconds = payload.error.retryAfterSeconds;
    return new AppException(
        AppErrorCode.GENERATION_CAPACITY_LIMITED,
        payload.error.message
            || 'Too many AI requests in the last minute. Please wait about 60 seconds and try again.',
        {
            retryable: payload.error.retryable === true,
            retryAfterMs: typeof retryAfterSeconds === 'number'
                ? retryAfterSeconds * 1000
                : undefined,
            reason: payload.error.category,
            context: {
                nextActions: payload.error.nextActions || ['retry_after_wait'],
                providerSubmitted: payload.error.providerSubmitted === true,
            },
        },
    );
};

const specialistUnavailableFromPayload = (
    payload: BackendStreamPayload | null,
): AppException | null => {
    if (payload?.error?.code !== 'SPECIALIST_UNAVAILABLE') return null;

    return new AppException(
        AppErrorCode.SPECIALIST_UNAVAILABLE,
        payload.error.message
            || 'This specialist is temporarily unavailable. Your request was not processed by another model.',
        {
            retryable: payload.error.retryable === true,
            reason: payload.error.category,
            context: {
                nextActions: payload.error.nextActions || ['retry_later'],
            },
        },
    );
};

const stripSseDataPrefix = (line: string): string | null => {
    const trimmed = line.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('data:')) {
        const data = trimmed.slice(5).trim();
        return data === '[DONE]' ? null : data;
    }
    return trimmed;
};

/**
 * ISSUE-1359: normalize mid-stream failures so undici engine internals
 * ("BodyStreamBuffer was aborted") never reach the user. A stream that dies
 * mid-body — server instance recycle, dropped connection, proxy timeout —
 * should surface as a clean retryable network error; an explicit caller
 * cancellation stays a cancellation.
 */
const normalizeStreamInterruption = (error: unknown, signal?: AbortSignal): unknown => {
    if (error instanceof AppException) return error;

    const isDomAbort = typeof DOMException !== 'undefined'
        && error instanceof DOMException
        && (error.name === 'AbortError' || error.name === 'NetworkError');
    const message = error instanceof Error ? error.message : String(error);
    const isBodyStreamAbort = /aborted|body stream|BodyStreamBuffer|network|fetch failed|terminated/i.test(message);

    if (signal?.aborted) {
        if (signal.reason === 'TIMEOUT') {
            return new AppException(AppErrorCode.TIMEOUT, 'AI Request timed out.', { retryable: true });
        }
        return new AppException(AppErrorCode.CANCELLED, 'AI Request was cancelled.', { retryable: false });
    }

    if (isDomAbort || isBodyStreamAbort) {
        logger.warn('[FirebaseIntelligenceService] Stream interrupted mid-body; surfacing retryable network error.', {
            err_name: error instanceof Error ? error.name : typeof error,
            err_msg: message.slice(0, 200),
        });
        return new AppException(
            AppErrorCode.NETWORK_ERROR,
            'The AI response stream was interrupted. Please retry.',
            { retryable: true, originalError: message },
        );
    }

    return error;
};

const extractFunctionCalls = (payload: BackendStreamPayload): FunctionCallPart['functionCall'][] => {
    if (Array.isArray(payload.functionCalls)) return payload.functionCalls;
    const parts = payload.candidates?.flatMap(candidate => candidate.content?.parts || []) || [];
    return parts
        .filter((part): part is FunctionCallPart => !!part && typeof part === 'object' && 'functionCall' in part)
        .map(part => part.functionCall);
};

const extractText = (payload: BackendStreamPayload): string => {
    if (typeof payload.text === 'string') return payload.text;
    const parts = payload.candidates?.flatMap(candidate => candidate.content?.parts || []) || [];
    return parts
        .map(part => ('text' in part && typeof part.text === 'string' ? part.text : ''))
        .join('');
};

// Re-export ChatMessage for backward compatibility
export type { ChatMessage } from './types';

// Default model if remote config fails
const FALLBACK_MODEL = INTELLIGENCE_MODELS.TEXT.FAST;
export class FirebaseIntelligenceService implements IntelligenceContext {
    public model: ExtendedGenerativeModel | null = null;
    private isInitialized = false;
    // Cost/runaway-output backstop: ~30 call sites across the agent/service layer
    // call generateContent(Stream) without their own maxOutputTokens, which left
    // every one of them with no ceiling at all (the Gemini API's own default is
    // effectively "as much as the model will produce"). Any caller that passes
    // its own maxOutputTokens still wins — this only fills the gap for the ones
    // that don't, via the `{ ...defaultConfig, ...config }` merge below.
    public defaultConfig: GenerationConfig = { maxOutputTokens: 8192 };

    public activeRequests: Map<string, Promise<GenerateContentResult>> = new Map();

    // File Service (Gemini API 2GB limit)
    public fileService = GeminiFileService.getInstance();

    // Match the strict backend generation window so a nine-seat Boardroom turn
    // is paced locally instead of self-throttling the shared gateway.
    public rateLimiter: RateLimiter = new RateLimiter(10, 1);

    // Circuit Breakers
    public contentBreaker = new CircuitBreaker(BREAKER_CONFIGS.CONTENT_GENERATION!);
    public mediaBreaker = new CircuitBreaker(BREAKER_CONFIGS.MEDIA_GENERATION!);
    public auxBreaker = new CircuitBreaker(BREAKER_CONFIGS.AUX_SERVICES!);

    // Dynamic Configuration
    public remoteConfig: RemoteIntelligenceConfig = DEFAULT_REMOTE_CONFIG;


    constructor() { }

    /**
     * Bootstrap the Intelligence service:
     * 1. Check if App Check is configured.
     * 2. Fetch Remote Config to get the latest model name.
     * 3. Initialize the GenerativeModel using the pre-configured Autonomous instance.
     */
    async bootstrap(): Promise<void> {
        if (this.isInitialized) return;

        // Check if App Check is configured. Do not fall back to raw browser keys.
        if (!isAppCheckConfigured()) {
            logger.warn('[FirebaseIntelligenceService] AI Verification Failed (App Check is not configured). Bypassing in local dev.');
        }

        try {
            // 1. Fetch Remote Config (Safe Mode) - NOW WITH DYNAMIC SCHEMA
            let modelName: string = FALLBACK_MODEL;
            try {
                if (!remoteConfig) {
                    throw new Error('remoteConfig not defined');
                }
                await fetchAndActivate(remoteConfig);

                // Get the old simple string (legacy)
                modelName = getValue(remoteConfig, 'model_name').asString() || FALLBACK_MODEL;

                // Get the new JSON config (modern)
                const jsonString = getValue(remoteConfig, 'ai_system_config').asString();
                if (jsonString) {
                    try {
                        const parsed = safeJsonParse(jsonString);
                        if (parsed) {
                            const validated = RemoteIntelligenceConfigSchema.safeParse(parsed);
                            if (validated.success) {
                                this.remoteConfig = validated.data;
                                logger.info('[FirebaseIntelligenceService] Loaded dynamic Autonomous config from Remote Config');
                            } else {
                                logger.warn('[FirebaseIntelligenceService] Invalid remote config schema:', validated.error);
                            }
                        }
                    } catch (e: unknown) {
                        logger.warn('[FirebaseIntelligenceService] Failed to parse ai_system_config JSON:', e);
                    }
                }
            } catch (configError: unknown) {
                if (isAppCheckError(configError)) {
                    if (import.meta.env.DEV) {
                        logger.warn('[FirebaseIntelligenceService] App Check Error fetching remote config. Bypassing in local dev.', configError);
                    } else {
                        logger.error('[FirebaseIntelligenceService] App Check failed in production. Failing closed.', configError);
                        throw configError;
                    }
                } else {
                    logger.warn('[FirebaseIntelligenceService] Failed to fetch remote config, using default model:', configError);
                }
            }

            // 2. Store the backend model name locally. The renderer no longer
            // initializes the Firebase AI SDK; all generation goes through the
            // secured Cloud Function gateway with Auth and App Check.
            this.model = { model: modelName };

            this.isInitialized = true;
            logger.info('[FirebaseIntelligenceService] Initialized with backend AI proxy');

        } catch (error: unknown) {
            logger.error('[FirebaseIntelligenceService] Bootstrap failed:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Get the model name, either from remote config or fallback
     * Handles DYNAMIC INTERCEPTION/REPLACEMENT of models.
     */
    /**
     * Decoupled, Capability-based Router and model resolver.
     * Resolves a capability name (e.g. 'text-agent'), model key (e.g. 'TEXT_AGENT'),
     * or model ID (e.g. 'gemini-3.1-pro-preview') to the active model ID,
     * respecting any Remote Config overrides.
     */
    public getModelName(modelOrCapability?: string): string {
        const candidate = modelOrCapability || this.model?.model || FALLBACK_MODEL;

        const CAPABILITY_TO_MODEL_KEY: Record<string, string> = {
            'text-agent': 'TEXT_AGENT',
            'text-fast': 'TEXT_FAST',
            'text-lite': 'TEXT_LITE',
            'image-generation': 'IMAGE_GEN',
            'image-fast': 'IMAGE_FAST',
            'video-generation': 'VIDEO_PRO',
            'video-fast': 'VIDEO_FAST',
            'video-lite': 'VIDEO_LITE',
            'audio-transcription': 'AUDIO_PRO',
            'audio-tts': 'AUDIO_TTS',
            'embedding': 'EMBEDDING_DEFAULT',
            'image-understanding': 'TEXT_AGENT',
            'video-understanding': 'TEXT_AGENT',
            'omni-multimodal': 'TEXT_AGENT'
        };

        const normalized = candidate.trim().toLowerCase();

        // If unified multimodal capability is enabled via config, route media analysis capabilities to the main agent model
        if (this.remoteConfig.config?.supportsUnifiedMultimodal) {
            if (
                normalized === 'audio-transcription' ||
                normalized === 'image-understanding' ||
                normalized === 'video-understanding' ||
                normalized === 'omni-multimodal'
            ) {
                const unifiedModelKey = 'TEXT_AGENT';
                const remoteOverride = this.remoteConfig.overrides[unifiedModelKey];
                const resolvedModel = remoteOverride || APPROVED_MODELS[unifiedModelKey];
                logger.info(`[FirebaseIntelligenceService] Routing multimodal capability "${normalized}" to unified model: ${resolvedModel}`);
                return resolvedModel;
            }
        }

        let modelKey: string | undefined;

        // 1. Check if the candidate is directly a mapped capability
        if (normalized in CAPABILITY_TO_MODEL_KEY) {
            modelKey = CAPABILITY_TO_MODEL_KEY[normalized];
        } else if (candidate in APPROVED_MODELS) {
            // 2. Check if the candidate is directly a model key (e.g., 'TEXT_AGENT')
            modelKey = candidate;
        } else {
            // 3. Otherwise try to reverse-lookup the model key from the model ID
            modelKey = getModelKey(candidate);
        }

        // 4. Resolve overrides or fallback to APPROVED_MODELS default
        if (modelKey && modelKey in APPROVED_MODELS) {
            const remoteOverride = this.remoteConfig.overrides[modelKey as keyof typeof APPROVED_MODELS];
            if (remoteOverride) {
                return remoteOverride;
            }
            return APPROVED_MODELS[modelKey as keyof typeof APPROVED_MODELS];
        }

        // 5. Fallback if not recognized as a capability or approved model
        return candidate;
    }

    private getBackendStreamUrl(): string {
        const projectId = auth.app?.options?.projectId || 'indii-music-founder';
        const isDev = import.meta.env.DEV;
        const useEmulator = import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === 'true';
        
        if (isDev && useEmulator) {
            return `http://127.0.0.1:5001/${projectId}/us-central1/generateContentStream`;
        }
        return `https://us-central1-${projectId}.cloudfunctions.net/generateContentStream`;
    }

    private async prepareBackendRequestHeaders(): Promise<Record<string, string>> {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new AppException(AppErrorCode.UNAUTHORIZED, 'User must be authenticated for AI requests.', { retryable: false });
        const getIdToken = currentUser.getIdToken;
        if (typeof getIdToken !== 'function' && import.meta.env.MODE !== 'test') {
            throw new AppException(AppErrorCode.UNAUTHORIZED, 'Authenticated user is missing an ID token provider.', { retryable: false });
        }
        const headers: Record<string, string> = {
            'content-type': 'application/json',
            authorization: `Bearer ${typeof getIdToken === 'function' ? await getIdToken.call(currentUser) : 'test-token'}`,
        };
        const activeAppCheck = appCheck || getAppCheck();
        if (!activeAppCheck) {
            if (import.meta.env.MODE === 'test') return { ...headers, 'x-firebase-appcheck': 'test-app-check-token' };
            // Soft-fail: let the backend's ENFORCE_APP_CHECK flag decide.
            // A placeholder or missing App Check key (e.g. local build deployed
            // to production) should not client-side block authenticated users.
            logger.warn('[FirebaseIntelligenceService] App Check unavailable — request will proceed with auth-only headers. Backend enforcement may reject it.');
            return headers;
        }
        try {
            let tokenResult = await getAppCheckToken(activeAppCheck, false);
            if (!tokenResult?.token) {
                tokenResult = await getAppCheckToken(activeAppCheck, true);
            }
            headers['x-firebase-appcheck'] = tokenResult.token;
        } catch (error: unknown) {
            // Soft-fail: log and proceed without App Check header
            logger.warn('[FirebaseIntelligenceService] App Check token retrieval failed — proceeding without App Check header.', error instanceof Error ? error.message : String(error));
        }
        return headers;
    }

    private async voidUnclaimedAgentStreamReservation(operationId: string): Promise<void> {
        if (!functions) throw new Error('Firebase Functions us-central1 client is unavailable.');
        await httpsCallable<{ operationId: string }, { voided: true }>(functions, 'voidAgentStreamCostReservation')({ operationId });
    }

    private async reserveAgentStream(userId: string, model: string, streaming: boolean, estimatedCost: number): Promise<string> {
        const costCheck = await CostControlService.checkAndReserve({
            operationType: 'agent_stream',
            estimatedCost,
            userId,
            metadata: { model, streaming },
        });
        if (!costCheck.allowed) {
            const unavailable = costCheck.reason?.includes('unavailable') || costCheck.reason?.includes('permission/auth check failed');
            throw new AppException(unavailable ? AppErrorCode.INTERNAL_ERROR : AppErrorCode.QUOTA_EXCEEDED,
                unavailable ? 'AI service temporarily unavailable' : costCheck.reason || 'Budget limit reached');
        }
        if (!costCheck.operationId) {
            throw new AppException(AppErrorCode.INTERNAL_ERROR, 'Cost ledger approved the request without a durable reservation id.');
        }
        return costCheck.operationId;
    }

    private async callBackendGenerateContentStream(
        contents: Content[],
        modelName: string,
        config: Record<string, unknown>,
        costReservationId: string,
        headers: Record<string, string>,
        signal?: AbortSignal
    ): Promise<{ stream: ReadableStream<StreamChunk>; response: Promise<WrappedResponse> }> {
        // Mirror the server's 200K-char contents guard locally so oversized
        // requests fail here with a specific PAYLOAD_TOO_LARGE instead of an
        // opaque HTTP 413 INTERNAL_ERROR round trip (ERROR_LEDGER 2026-08-27).
        assertContentsWithinStreamBudget(contents, 'generateContentStream');
        const response = await fetch(this.getBackendStreamUrl(), {
            method: 'POST',
            headers,
            body: JSON.stringify({ model: modelName, contents, config, costReservationId }),
            signal
        });

        if (!response.ok || !response.body) {
            const message = await response.text().catch(() => response.statusText);
            const specialistUnavailable = specialistUnavailableFromPayload(
                safeJsonParse(message) as BackendStreamPayload | null,
            );
            if (specialistUnavailable) throw specialistUnavailable;
            const generationCapacity = generationCapacityFromPayload(
                safeJsonParse(message) as BackendStreamPayload | null,
            );
            if (generationCapacity) throw generationCapacity;
            if (response.status === 413) {
                // The server's contents guard rejected the serialized request.
                // Name the actual failure instead of masking it as INTERNAL_ERROR.
                throw new AppException(AppErrorCode.PAYLOAD_TOO_LARGE,
                    message || 'AI request payload exceeded the backend size limit.',
                    { retryable: false, context: { httpStatus: response.status } });
            }
            if (response.status === 429) {
                throw new AppException(AppErrorCode.QUOTA_EXCEEDED,
                    message || 'AI service quota exhausted. Please try again shortly.',
                    { retryable: true, context: { httpStatus: 429 } });
            }
            if (response.status === 401 || response.status === 403) {
                throw new AppException(AppErrorCode.UNAUTHORIZED, message || 'AI backend authentication failed', { retryable: false });
            }
            throw new AppException(AppErrorCode.INTERNAL_ERROR, message || `AI backend failed with HTTP ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let finalText = '';
        let receivedComplete = false;
        const chunks: StreamChunk[] = [];
        let resolveWrappedResponse!: (response: WrappedResponse) => void;
        let rejectWrappedResponse!: (error: unknown) => void;
        const wrappedResponsePromise = new Promise<WrappedResponse>((resolve, reject) => {
            resolveWrappedResponse = resolve;
            rejectWrappedResponse = reject;
        });

        const buildWrappedResponse = (): WrappedResponse => {
            const responsePayload: GenerateContentResponse = {
                candidates: [{
                    content: {
                        role: 'model',
                        parts: finalText ? [{ text: finalText }] : []
                    }
                }],
                text: () => finalText,
                functionCalls: () => chunks.flatMap(chunk => chunk.functionCalls?.() || [])
            };

            return {
                response: responsePayload,
                text: () => finalText,
                functionCalls: () => chunks.flatMap(chunk => chunk.functionCalls?.() || []),
                usage: () => undefined,
                thoughtSignature: chunks.find(chunk => chunk.thoughtSignature)?.thoughtSignature
            };
        };

        const transformedStream = new ReadableStream<StreamChunk>({
            async start(controller) {
                try {
                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;
                        buffer += decoder.decode(value, { stream: true });
                        const lines = buffer.split('\n');
                        buffer = lines.pop() || '';

                        for (const line of lines) {
                            if (!line.trim()) continue;
                            const jsonLine = stripSseDataPrefix(line);
                            if (!jsonLine) continue;
                            const parsed = safeJsonParse(jsonLine) as BackendStreamPayload | null;
                            if (!parsed) continue;
                            if (parsed.complete === true) { receivedComplete = true; continue; }
                            const specialistUnavailable = specialistUnavailableFromPayload(parsed);
                            if (specialistUnavailable) throw specialistUnavailable;
                            const text = extractText(parsed);
                            finalText += text;
                            const chunk: StreamChunk = {
                                text: () => text,
                                functionCalls: () => extractFunctionCalls(parsed),
                                thoughtSignature: parsed.thoughtSignature
                            };
                            chunks.push(chunk);
                            controller.enqueue(chunk);
                        }
                    }
                    if (buffer.trim()) {
                        const jsonLine = stripSseDataPrefix(buffer);
                        const parsed = jsonLine ? safeJsonParse(jsonLine) as BackendStreamPayload | null : null;
                        if (parsed) {
                            if (parsed.complete === true) { receivedComplete = true; }
                            else {
                            const specialistUnavailable = specialistUnavailableFromPayload(parsed);
                            if (specialistUnavailable) throw specialistUnavailable;
                            const text = extractText(parsed);
                            finalText += text;
                            const chunk: StreamChunk = {
                                text: () => text,
                                functionCalls: () => extractFunctionCalls(parsed),
                                thoughtSignature: parsed.thoughtSignature
                            };
                            chunks.push(chunk);
                            controller.enqueue(chunk);
                            }
                        }
                    }
                    if (!receivedComplete) throw new AppException(AppErrorCode.NETWORK_ERROR, 'AI stream ended before billing settlement completed.', { retryable: true });
                    controller.close();
                    const finalResponse = buildWrappedResponse();
                    logger.debug(`[FirebaseIntelligenceService] Stream completed with ${finalResponse.functionCalls()?.length ?? 0} function calls`);
                    resolveWrappedResponse(finalResponse);
                } catch (error) {
                    // ISSUE-1359: a stream that dies mid-body (server instance
                    // recycle, network drop, proxy timeout) surfaces undici's
                    // raw "BodyStreamBuffer was aborted" DOMException. That raw
                    // engine text leaked into Boardroom verdicts as a confusing
                    // "system error" instead of a clean, retryable message.
                    // Normalize stream-interruption errors here so the agent
                    // never renders undici internals to the user.
                    const normalized = normalizeStreamInterruption(error, signal);
                    try { controller.error(normalized); } catch { /* cancellation already closed controller */ }
                    rejectWrappedResponse(normalized);
                }
            },
            async cancel(reason) {
                await reader.cancel(reason);
            }
        });

        return { stream: transformedStream, response: wrappedResponsePromise };
    }

    async rawGenerateContent(
        prompt: string | Content[],
        modelOverride?: string,
        config?: GenerationConfig,
        systemInstruction?: string,
        tools?: Tool[],
        options?: {
            signal?: AbortSignal;
            cachedContent?: string;
            safetySettings?: SafetySetting[];
            toolConfig?: ToolConfig;
            thoughtSignature?: string;
            skipCache?: boolean;
            timeout?: number;
        }
    ): Promise<GenerateContentResult> {
        const modelName = this.getModelName(modelOverride);
        const mergedConfig = { ...this.defaultConfig, ...config };

        // SAFETY: Extract non-generation fields that callers may have mixed into config.
        // e.g. onboardingService passes { systemInstruction, tools, ...thinkingConfig } as config.
        // These must NOT end up in generationConfig or the API will reject with 400.
        const configRecord = mergedConfig as Record<string, unknown>;
        if (!systemInstruction && typeof configRecord.systemInstruction === 'string') {
            systemInstruction = configRecord.systemInstruction as string;
        }
        if (!tools && Array.isArray(configRecord.tools)) {
            tools = configRecord.tools as Tool[];
        }
        delete configRecord.systemInstruction;
        delete configRecord.tools;
        delete configRecord.toolConfig;
        delete configRecord.safetySettings;

        // Remove thinkingConfig for models that don't support it (e.g., gemini-2.5-pro)
        // Only Gemini 3 family supports thinking — gemini-2.0-pro-exp was removed per MODEL_POLICY.md
        const supportsThinking = modelName.includes('gemini-3');
        if (!supportsThinking) {
            delete configRecord.thinkingConfig;
        }

        // 1. Request Coalescing & Cache Key
        // Create a lean key that avoids stringifying large binary data
        const leanPrompt = Array.isArray(prompt)
            ? prompt.map(p => ({
                ...p,
                parts: p.parts.map(part => {
                    if ('inlineData' in part && part.inlineData) {
                        const { mimeType, data } = part.inlineData;
                        const dataLen = data?.length || 0;
                        const snippet = dataLen > 32
                            ? `${data.substring(0, 16)}...${data.substring(dataLen - 16)}`
                            : data;
                        return { ...part, inlineData: { mimeType, data: `[REDACTED:${mimeType}:${dataLen}:${snippet}]` } };
                    }
                    return part;
                })
            }))
            : typeof prompt === 'string' ? prompt : '[COMPLEX_OBJECT]';

        const cacheKey = JSON.stringify({ leanPrompt, modelName, config: mergedConfig, systemInstruction });

        if (!options?.skipCache && this.activeRequests.has(cacheKey)) {
            // logger.debug(`[FirebaseIntelligenceService] Coalescing request for ${modelName}`);
            return this.activeRequests.get(cacheKey)!;
        }

        const executeRequest = async (): Promise<GenerateContentResult> => {
            // Create an internal AbortController for timeout if specified
            const timeoutController = new AbortController();
            let timeoutId: NodeJS.Timeout | number | undefined;
            let externalAbortHandler: (() => void) | undefined;

            // ISSUE-1367 (founder-live): the old 25s default aborted agent
            // reasoning streams mid-flight — the timer covers the WHOLE stream
            // (rate-limiter queue wait + pre-flight + token stream), and the
            // server contract for generateContentStream is timeoutSeconds: 300.
            // A 3-seat swarm turn or a slow Vertex model routinely exceeds 25s;
            // the abort surfaced as "AI Request timed out after 25000ms" and the
            // agent misattributed it to the image engine (generateImageV3 never
            // received the request). 120s bounds runaway calls while fitting the
            // real server capability.
            const requestTimeout = options?.timeout || 120_000;
            if (requestTimeout > 0) {
                timeoutId = setTimeout(() => {
                    timeoutController.abort('TIMEOUT');
                }, requestTimeout);
            }

            // Combine with user signal if provided
            if (options?.signal) {
                if (options.signal.aborted) {
                    timeoutController.abort(options.signal.reason || 'CANCELLED');
                } else {
                    externalAbortHandler = () => timeoutController.abort(options.signal?.reason || 'CANCELLED');
                    options.signal.addEventListener('abort', externalAbortHandler, { once: true });
                }
            }

            const internalSignal = timeoutController.signal;
            let costReservationId: string | undefined;
            let headers: Record<string, string> | undefined;

            try {
                await this.rateLimiter.acquire(300_000, internalSignal);
                return this.contentBreaker.execute(async () => {
                    return this.withRetry(async () => {
                        // Check if already aborted
                        if (internalSignal.aborted) {
                            if (internalSignal.reason === 'TIMEOUT') {
                                throw new AppException(AppErrorCode.TIMEOUT, `AI Request timed out after ${requestTimeout}ms`);
                            }
                            throw new AppException(AppErrorCode.CANCELLED, 'AI Request was cancelled by user');
                        }
                        await this.ensureInitialized();

                        // 1. Explicit mock mode is not a supported runtime path.
                        if (import.meta.env.VITE_INTELLIGENCE_MOCK_MODE === 'true') {
                            throw new AppException(AppErrorCode.INTERNAL_ERROR, 'VITE_INTELLIGENCE_MOCK_MODE=true is no longer supported. Configure live intelligence services.');
                        }

                        // 2. Quota & Rate Limit
                        const userId = auth.currentUser?.uid;
                        if (!userId) throw new AppException(AppErrorCode.UNAUTHORIZED, 'User must be authenticated for AI requests.', { retryable: false });
                        {
                            // ISSUE-1361 (Boardroom latency): quota and rate-limit
                            // checks are independent Firestore reads — run them in
                            // parallel instead of serially. The cost reservation
                            // (server round trip) and the backend request headers
                            // (ID-token + App-Check mints) are also independent of
                            // each other, so they run concurrently too. This cuts
                            // the pre-stream critical path from four serial awaits
                            // to two, which is the dominant Boardroom
                            // message→first-token delay for every seated agent.
                            const [_quotaResult, _rateLimitResult] = await Promise.all([
                                TokenUsageService.checkQuota(userId),
                                TokenUsageService.checkRateLimit(userId),
                            ]);

                            const [reservationId, preparedHeaders] = await Promise.all([
                                this.reserveAgentStream(userId, modelName, false,
                                    estimateAgentStreamCost(prompt, systemInstruction, tools, modelName, mergedConfig.maxOutputTokens)),
                                this.prepareBackendRequestHeaders(),
                            ]);
                            costReservationId = reservationId;
                            headers = preparedHeaders;
                        }

                        // 4. Sanitize & Prepare Prompt
                        const sanitizedPrompt = this.sanitizePrompt(prompt);

                        // 5. Inject thoughtSignature if present (Critical for Gemini 3 function calling)
                        if (options?.thoughtSignature && Array.isArray(sanitizedPrompt) && sanitizedPrompt.length > 0) {
                            const lastContent = sanitizedPrompt[sanitizedPrompt.length - 1]!;
                            if (lastContent.parts.length > 0) {
                                const lastPartIdx = lastContent.parts.length - 1;
                                (lastContent.parts[lastPartIdx] as ContentPart).thoughtSignature = options.thoughtSignature;
                            }
                        }

                        // 6. Auto-detect high-fidelity media requirements for audio payloads
                        let hasAudio = false;
                        const contentsToCheck = Array.isArray(sanitizedPrompt)
                            ? sanitizedPrompt
                            : [{ parts: [{ text: sanitizedPrompt as string }] }];

                        for (const c of contentsToCheck as Content[]) {
                            if (c.parts) {
                                for (const p of c.parts) {
                                    if (('inlineData' in p && p.inlineData && p.inlineData.mimeType?.startsWith('audio/')) ||
                                        ('fileData' in p && (p as unknown as FileDataPart).fileData && (p as unknown as FileDataPart).fileData.mimeType?.startsWith('audio/'))) {
                                        hasAudio = true;
                                        break;
                                    }
                                }
                            }
                            if (hasAudio) break;
                        }

                        if (hasAudio && !mergedConfig.mediaResolution) {
                            mergedConfig.mediaResolution = 'MEDIA_RESOLUTION_HIGH';
                        }

                        const contents: Content[] = typeof sanitizedPrompt === 'string'
                            ? [{ role: 'user' as const, parts: [{ text: sanitizedPrompt }] }]
                            : sanitizedPrompt as Content[];
                        const backendConfig: Record<string, unknown> = {
                            ...mergedConfig,
                            systemInstruction,
                            tools,
                            toolConfig: options?.toolConfig,
                            safetySettings: options?.safetySettings || STANDARD_SAFETY_SETTINGS
                        };
                        const streamResult = await this.callBackendGenerateContentStream(contents, modelName, backendConfig, costReservationId, headers, internalSignal);
                        const reader = streamResult.stream.getReader();
                        while (!(await reader.read()).done) {
                            // Drain the stream so the backend response promise has the final text.
                        }
                        const result = await streamResult.response;

                        // 8. Track Usage
                        if (userId && result?.response?.usageMetadata) {
                            await TokenUsageService.trackUsage(
                                userId,
                                modelName,
                                result.response.usageMetadata.promptTokenCount || 0,
                                result.response.usageMetadata.candidatesTokenCount || 0
                            );
                        }

                        return result;
                    }, 0, 1000, internalSignal);
                });
            } catch (error) {
                if (costReservationId) {
                    try { await this.voidUnclaimedAgentStreamReservation(costReservationId); }
                    catch (releaseError) { logger.warn('[FirebaseIntelligenceService] Reservation release deferred to gateway recovery.', { operationId: costReservationId, releaseError }); }
                }
                if (internalSignal.aborted) {
                    if (internalSignal.reason === 'TIMEOUT') {
                        throw new AppException(AppErrorCode.TIMEOUT, `AI Request timed out after ${requestTimeout}ms`);
                    }
                    throw new AppException(AppErrorCode.CANCELLED, 'AI Request was cancelled by user');
                }
                throw error;
            } finally {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                if (externalAbortHandler && options?.signal) {
                    options.signal.removeEventListener('abort', externalAbortHandler);
                }
            }
        };

        const requestPromise = executeRequest();
        this.activeRequests.set(cacheKey, requestPromise);
        // Attach a secondary .catch to the .finally() chain to suppress duplicate
        // UnhandledPromiseRejection — the error is already surfaced via the returned
        // requestPromise. This suppression is intentional, not a silent swallow.
        requestPromise.finally(() => {
            if (this.activeRequests.get(cacheKey) === requestPromise) {
                this.activeRequests.delete(cacheKey);
            }
        }).catch((err: unknown) => {
            // Error already propagated to caller; suppress secondary rejection on the
            // .finally() sub-chain to avoid duplicate UnhandledPromiseRejection events.
            logger.debug('[FirebaseIntelligenceService] Suppressed duplicate rejection on pool cleanup:', err);
        });

        return requestPromise;
    }

    /**
     * CORE: Raw generate content stream
     */
    async rawGenerateContentStream(
        prompt: string | Content[],
        modelOverride?: string,
        config?: GenerationConfig,
        systemInstruction?: string,
        tools?: Tool[],
        options?: {
            signal?: AbortSignal;
            safetySettings?: SafetySetting[];
            toolConfig?: ToolConfig;
            thoughtSignature?: string;
            timeout?: number;
        }
    ): Promise<{ stream: ReadableStream<StreamChunk>; response: Promise<WrappedResponse> }> {
        const modelName = this.getModelName(modelOverride);
        const mergedConfig = { ...this.defaultConfig, ...config };

        // SAFETY: Extract non-generation fields that callers may have mixed into config.
        const streamConfigRecord = mergedConfig as Record<string, unknown>;
        if (!systemInstruction && typeof streamConfigRecord.systemInstruction === 'string') {
            systemInstruction = streamConfigRecord.systemInstruction as string;
        }
        if (!tools && Array.isArray(streamConfigRecord.tools)) {
            tools = streamConfigRecord.tools as Tool[];
        }
        delete streamConfigRecord.systemInstruction;
        delete streamConfigRecord.tools;
        delete streamConfigRecord.toolConfig;
        delete streamConfigRecord.safetySettings;

        // Remove thinkingConfig for models that don't support it
        // Only Gemini 3 family supports thinking — gemini-2.0-pro-exp was removed per MODEL_POLICY.md
        const supportsThinking = modelName.includes('gemini-3');
        if (!supportsThinking) {
            delete streamConfigRecord.thinkingConfig;
        }

        // Create an internal AbortController for timeout if specified
        const timeoutController = new AbortController();
        let timeoutId: NodeJS.Timeout | number | undefined;
        let externalAbortHandler: (() => void) | undefined;

        // ISSUE-1367: same 25s→120s raise as the non-stream path — this timer
        // covers the entire stream (queue + pre-flight + tokens), and the
        // generateContentStream callable allows 300s server-side.
        const requestTimeout = options?.timeout || 120_000;
        if (requestTimeout > 0) {
            timeoutId = setTimeout(() => {
                timeoutController.abort('TIMEOUT');
            }, requestTimeout);
        }

        // Combine with user signal if provided
        if (options?.signal) {
            if (options.signal.aborted) {
                timeoutController.abort(options.signal.reason || 'CANCELLED');
            } else {
                externalAbortHandler = () => timeoutController.abort(options.signal?.reason || 'CANCELLED');
                options.signal.addEventListener('abort', externalAbortHandler, { once: true });
            }
        }

        const internalSignal = timeoutController.signal;
        let costReservationId: string | undefined;
        let headers: Record<string, string> | undefined;
        const cleanupRequestLifecycle = () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = undefined;
            }
            if (externalAbortHandler && options?.signal) {
                options.signal.removeEventListener('abort', externalAbortHandler);
                externalAbortHandler = undefined;
            }
        };

        try {
            await this.rateLimiter.acquire(300_000, internalSignal);
            const streamResult = await this.contentBreaker.execute(async () => {
                return this.withRetry(async () => {
                    if (internalSignal.aborted) {
                        if (internalSignal.reason === 'TIMEOUT') {
                            throw new AppException(AppErrorCode.TIMEOUT, `AI Request timed out after ${requestTimeout}ms`);
                        }
                        throw new AppException(AppErrorCode.CANCELLED, 'AI Request was cancelled by user');
                    }
                    await this.ensureInitialized();

                    // 1. Explicit mock mode is not a supported runtime path.
                    if (import.meta.env.VITE_INTELLIGENCE_MOCK_MODE === 'true') {
                        throw new AppException(AppErrorCode.INTERNAL_ERROR, 'VITE_INTELLIGENCE_MOCK_MODE=true is no longer supported. Configure live intelligence services.');
                    }

                    // 2. Quota & Rate Limit (Backend)
                    const userId = auth.currentUser?.uid;
                    if (!userId) throw new AppException(AppErrorCode.UNAUTHORIZED, 'User must be authenticated for AI requests.', { retryable: false });
                    {
                        // ISSUE-1361: same parallel pre-flight as the stream
                        // path — quota/rate-limit reads and the reservation +
                        // header mints are independent, so run them
                        // concurrently to cut the pre-token critical path.
                        const [_quotaResult, _rateLimitResult] = await Promise.all([
                            TokenUsageService.checkQuota(userId),
                            TokenUsageService.checkRateLimit(userId),
                        ]);

                        const [reservationId, preparedHeaders] = await Promise.all([
                            this.reserveAgentStream(userId, modelName, true,
                                estimateAgentStreamCost(prompt, systemInstruction, tools, modelName, mergedConfig.maxOutputTokens)),
                            this.prepareBackendRequestHeaders(),
                        ]);
                        costReservationId = reservationId;
                        headers = preparedHeaders;
                    }

                    // 2. Sanitize & Prepare Prompt
                    const sanitizedPrompt = this.sanitizePrompt(prompt);

                    // 3. Inject thoughtSignature if present
                    if (options?.thoughtSignature && Array.isArray(sanitizedPrompt) && sanitizedPrompt.length > 0) {
                        const lastContent = sanitizedPrompt[sanitizedPrompt.length - 1]!;
                        if (lastContent.parts.length > 0) {
                            const lastPartIdx = lastContent.parts.length - 1;
                            (lastContent.parts[lastPartIdx] as ContentPart).thoughtSignature = options.thoughtSignature;
                        }
                    }

                    const contents: Content[] = typeof sanitizedPrompt === 'string'
                        ? [{ role: 'user' as const, parts: [{ text: sanitizedPrompt }] }]
                        : sanitizedPrompt as Content[];
                    const backendConfig: Record<string, unknown> = {
                        ...mergedConfig,
                        systemInstruction,
                        tools,
                        toolConfig: options?.toolConfig,
                        safetySettings: options?.safetySettings || STANDARD_SAFETY_SETTINGS
                    };
                    return this.callBackendGenerateContentStream(contents, modelName, backendConfig, costReservationId, headers, internalSignal);
                }, 0, 1000, internalSignal);
            });
            return {
                stream: streamResult.stream,
                response: streamResult.response.finally(cleanupRequestLifecycle),
            };
        } catch (error) {
            cleanupRequestLifecycle();
            if (costReservationId) {
                try { await this.voidUnclaimedAgentStreamReservation(costReservationId); }
                catch (releaseError) { logger.warn('[FirebaseIntelligenceService] Reservation release deferred to gateway recovery.', { operationId: costReservationId, releaseError }); }
            }
            if (internalSignal.aborted) {
                if (internalSignal.reason === 'TIMEOUT') {
                    throw new AppException(AppErrorCode.TIMEOUT, `AI Request timed out after ${requestTimeout}ms`);
                }
                throw new AppException(AppErrorCode.CANCELLED, 'AI Request was cancelled by user');
            }
            throw error;
        }
    }

    /**
     * CORE: Generate content and return Result Object (Used by AIService)
     */
    async generateContent(
        prompt: string | Content[],
        modelOverride?: string,
        config?: GenerationConfig,
        systemInstruction?: string,
        tools?: Tool[],
        options?: { signal?: AbortSignal, safetySettings?: SafetySetting[], toolConfig?: ToolConfig, thoughtSignature?: string, timeout?: number }
    ): Promise<GenerateContentResult> {
        logger.debug('[FirebaseIntelligenceService] generateContent request');

        return this.rawGenerateContent(prompt, modelOverride, config, systemInstruction, tools, options);
    }

    /**
     * CORE: Generate content stream(Used by AIService)
     */
    async generateContentStream(
        prompt: string | Content[],
        modelOverride?: string,
        config?: GenerationConfig,
        systemInstruction?: string,
        tools?: Tool[],
        options?: { signal?: AbortSignal, safetySettings?: SafetySetting[], toolConfig?: ToolConfig }
    ): Promise<{ stream: ReadableStream<StreamChunk>, response: Promise<WrappedResponse> }> {
        return this.rawGenerateContentStream(prompt, modelOverride, config, systemInstruction, tools, options);
    }

    /**
     * HIGH LEVEL: Generate text with optional thinking budget and system instruction.
     * @see generators/HighLevelAPI.ts
     */
    async generateText(
        prompt: string | Part[],
        thinkingBudgetOrModelOrConfig?: number | string | Record<string, unknown>,
        systemInstructionOrConfig?: string | Record<string, unknown>
    ): Promise<string> {
        return hlGenerateText(this, prompt, thinkingBudgetOrModelOrConfig, systemInstructionOrConfig);
    }

    /**
     * HIGH LEVEL: Generate structured data from a prompt/parts and schema.
     * @see generators/HighLevelAPI.ts
     */
    async generateStructuredData<T>(
        promptOrOptions: string | Part[] | GenerateContentOptions,
        schemaOrConfig?: Schema | Record<string, unknown>,
        thinkingBudgetOrConfig?: number | Record<string, unknown>,
        systemInstruction?: string,
        modelOverride?: string
    ): Promise<T> {
        return hlGenerateStructuredData<T>(this, promptOrOptions, schemaOrConfig, thinkingBudgetOrConfig, systemInstruction, modelOverride);
    }

    /**
     * HIGH LEVEL: Multi-turn chat.
     * @see generators/HighLevelAPI.ts
     */
    async chat(
        history: ChatMessage[],
        newMessage: string,
        systemInstruction?: string
    ): Promise<string> {
        return hlChat(this, history, newMessage, systemInstruction);
    }

    /**
     * MULTIMODAL: Analyze an image (base64).
     * @see generators/HighLevelAPI.ts
     */
    async analyzeImage(
        prompt: string,
        imageBase64: string,
        mimeType: string = 'image/jpeg'
    ): Promise<string> {
        return hlAnalyzeImage(this, prompt, imageBase64, mimeType);
    }

    /**
     * MULTIMODAL: Analyze generic parts (Video, Audio, PDF).
     * @see generators/HighLevelAPI.ts
     */
    async analyzeMultimodal(
        prompt: string,
        parts: Part[]
    ): Promise<string> {
        return hlAnalyzeMultimodal(this, prompt, parts);
    }

    /**
     * ADVANCED: Grounding with Google Search.
     * @see generators/HighLevelAPI.ts
     */
    async generateGroundedContent(prompt: string, options?: { dynamicThreshold?: number }): Promise<GenerateContentResult> {
        return hlGenerateGroundedContent(this, prompt, options);
    }

    /**
     * MULTIMODAL: Caption an image.
     * @see generators/HighLevelAPI.ts
     */
    async captionImage(image: string | ArrayBuffer, prompt: string = 'Describe this image in detail...'): Promise<string> {
        return hlCaptionImage(this, image, prompt);
    }

    /**
     * HIGH LEVEL: Analyze audio content.
     * @see generators/HighLevelAPI.ts
     */
    async analyzeAudio(audio: string | ArrayBuffer, prompt: string = 'Analyze this audio content in detail...'): Promise<string> {
        return hlAnalyzeAudio(this, audio, prompt);
    }

    /**
     * MULTIMODAL: Analyze a file URI (Gemini Files API).
     * @see generators/HighLevelAPI.ts
     */
    async analyzeFileURI(
        fileUri: string,
        mimeType: string,
        prompt: string = 'Analyze this document in detail...'
    ): Promise<string> {
        return hlAnalyzeFileURI(this, fileUri, mimeType, prompt);
    }

    /**
     * UTILITY: Parse JSON from Intelligence response, handling markdown code blocks.
     * @see generators/HighLevelAPI.ts
     */
    parseJSON<T = Record<string, unknown>>(text: string | undefined): T | Record<string, never> {
        return hlParseJSON<T>(text);
    }

    /**
     * ADVANCED: Live API for real-time bi-directional communication.
     * NOTE: Live API is not available in fallback mode (requires Firebase Autonomous SDK with App Check)
     */
    async getLiveModel(systemInstruction?: string): Promise<LiveGenerativeModel> {
        void systemInstruction;
        await this.ensureInitialized();
        throw new AppException(
            AppErrorCode.UNAUTHORIZED,
            'Browser-side Firebase AI Live API is disabled. Use a secured backend realtime gateway.',
            { retryable: false }
        );
    }

    /**
     * Browser-side direct video generation is disabled. Use
     * VideoGenerationService, which routes through the secured generateVideoV3
     * callable Cloud Function.
     */
    async generateVideo(options: GenerateVideoRequest & { 
        timeoutMs?: number; 
        onProgress?: (status: 'queued' | 'processing', attempt: number, maxAttempts: number) => void;
    }): Promise<string> {
        logger.warn('[FirebaseAI] Blocked browser-side direct video generation request.', {
            model: options.model || '(default)',
            promptLength: options.prompt?.length ?? 0,
        });
        throw new AppException(
            AppErrorCode.UNAUTHORIZED,
            'Client-side video generation is disabled. Use the secured generateVideoV3 Cloud Function gateway.',
            { retryable: false }
        );
    }

    /**
     * BATCHING: Embed multiple documents in parallel.
     * @see generators/EmbeddingGenerator.ts
     */
    async batchEmbedContents(
        contentsOrStrings: Content[] | string[],
        modelOverride?: string
    ): Promise<number[][]> {
        return embeddingBatchEmbed(this, contentsOrStrings, modelOverride);
    }

    /**
     * HIGH LEVEL: Generate image using Gemini 3 native image generation.
     * @see generators/ImageGenerator.ts
     */
    async generateImage(promptOrOptions: string | GenerateImageOptions, modelOverride?: string, configOverride?: GenerationConfig): Promise<string> {
        return imgGenerate(this, promptOrOptions, modelOverride, configOverride);
    }

    /**
     * TTS: Generate speech from text.
     * @see generators/SpeechGenerator.ts
     */
    async generateSpeech(
        text: string,
        voice: string = 'Kore',
        modelOverride?: string
    ): Promise<GenerateSpeechResponse> {
        return speechGenerate(this, text, voice, modelOverride);
    }

    /**
     * CORE: Embed content.
     * @see generators/EmbeddingGenerator.ts
     */
    async embedContent(options: { model: string; content: Content }): Promise<{ values: number[] }> {
        return embeddingEmbed(this, options);
    }

    public async ensureInitialized() {
        if (!this.isInitialized) {
            await this.bootstrap();
        }
        if (!this.model) {
            throw new AppException(AppErrorCode.INTERNAL_ERROR, 'Intelligence Service not properly initialized');
        }
    }

    public handleError(error: unknown): AppException {
        if (error instanceof AppException) return error;
        const msg = error instanceof Error ? error.message : String(error);

        // Handle abort signals explicitly (these are retryable)
        if (msg.includes('aborted') || msg.includes('signal is aborted') || msg.includes('AbortError')) {
            return new AppException(
                AppErrorCode.CANCELLED,
                'AI Request was cancelled or timed out. Please try again.',
                { retryable: true }
            );
        }

        // Handle Firebase Installations API specific errors (missing API config)
        if (msg.includes('Installations') || msg.includes('installations')) {
            return new AppException(
                AppErrorCode.INTERNAL_ERROR,
                'Firebase Installations API is disabled or restricted. Please enable it in Google Cloud Console.',
                { originalError: msg }
            );
        }

        const lowerMsg = msg.toLowerCase();
        if (
            lowerMsg.includes('permission-denied') ||
            lowerMsg.includes('permission_denied') ||
            lowerMsg.includes('app-check-token') ||
            lowerMsg.includes('unauthorized')
        ) {
            return new AppException(AppErrorCode.UNAUTHORIZED, 'AI Verification Failed (App Check/Auth)', { retryable: false });
        }
        if (msg.includes('Recaptcha')) {
            return new AppException(AppErrorCode.UNAUTHORIZED, 'Client Verification Failed (ReCaptcha)');
        }

        // Detailed Quota & Rate Limit Mapping
        if (msg.includes('quota') || msg.includes('resource-exhausted')) {
            return new AppException(AppErrorCode.QUOTA_EXCEEDED, 'AI Quota Exceeded');
        }
        if (msg.includes('429') || lowerMsg.includes('rate limit')) {
            return new AppException(AppErrorCode.RATE_LIMITED, 'AI Rate Limit Exceeded', { retryable: true });
        }

        // Service Availability
        if (msg.includes('503') || msg.includes('500') || lowerMsg.includes('service unavailable') || lowerMsg.includes('overloaded') || lowerMsg.includes('internal error')) {
            return new AppException(AppErrorCode.NETWORK_ERROR, 'Intelligence Service Temporarily Unavailable or Internal Error', { retryable: true });
        }

        // Backend/provider API errors — check HTTP status code directly
        const httpStatus = (error as { status?: number })?.status;
        if (httpStatus === 404) {
            return new AppException(
                AppErrorCode.INTERNAL_ERROR,
                'AI Model Not Found — the requested model is not available on this API endpoint. Check model configuration.',
                { retryable: false, originalError: msg }
            );
        }
        if (httpStatus === 400) {
            return new AppException(
                AppErrorCode.INTERNAL_ERROR,
                `AI Bad Request — invalid model or request configuration. ${msg}`,
                { retryable: false, originalError: msg }
            );
        }
        if (httpStatus === 429) {
            return new AppException(AppErrorCode.RATE_LIMITED, 'AI Rate Limit Exceeded', { retryable: true });
        }
        if (httpStatus === 503 || httpStatus === 500) {
            return new AppException(AppErrorCode.NETWORK_ERROR, 'Intelligence Service Temporarily Unavailable', { retryable: true });
        }

        return new AppException(AppErrorCode.INTERNAL_ERROR, `Intelligence Service Failure: ${msg}`, { retryable: false });
    }

    public async withRetry<T>(
        operation: () => Promise<T>,
        retries = 3,
        initialDelay = 1000,
        signal?: AbortSignal
    ): Promise<T> {
        let lastError: unknown;

        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                if (signal?.aborted) {
                    const reason = signal.reason === 'TIMEOUT' ? 'AI Request timed out' : (signal.reason || 'Operation cancelled by user');
                    throw typeof reason === 'string' ? new Error(reason) : reason;
                }
                return await operation();
            } catch (error: unknown) {
                lastError = error;

                if (signal?.aborted) {
                    throw error;
                }

                const appException = this.handleError(error);
                // Specialist availability is manually retryable, but automatic
                // retries could resubmit a prompt after the provider accepted it
                // and failed before the first chunk. Preserve the user's request
                // for an explicit retry instead of risking duplicate spend/work.
                const isRetryable = appException.details?.retryable
                    && appException.code !== AppErrorCode.SPECIALIST_UNAVAILABLE
                    && appException.code !== AppErrorCode.GENERATION_CAPACITY_LIMITED;

                if (attempt < retries && isRetryable) {
                    // Exponential backoff with jitter
                    const backoff = (initialDelay * Math.pow(2, attempt)) + secureRandomInt(0, 200);
                    const waitTime = Math.min(backoff, 15000); // Absolute cap at 15s

                    logger.warn(`[FirebaseIntelligenceService] Transient error, retrying in ${Math.round(waitTime)}ms... (Attempt ${attempt + 1}/${retries})`);

                    await new Promise((resolve, reject) => {
                        const handleAbort = () => {
                            clearTimeout(timer);
                            reject(new Error('Operation cancelled by user during retry backoff'));
                        };
                        const timer = setTimeout(() => {
                            signal?.removeEventListener('abort', handleAbort);
                            resolve(undefined);
                        }, waitTime);
                        if (signal) {
                            signal.addEventListener('abort', handleAbort, { once: true });
                        }
                    });

                    continue;
                }
                throw appException;
            }
        }
        throw this.handleError(lastError);
    }

    public sanitizePrompt(prompt: string | Content[]): string | Content[] {
        if (typeof prompt === 'string') {
            return InputSanitizer.sanitize(prompt);
        }

        if (Array.isArray(prompt)) {
            return prompt.map(content => ({
                role: content.role || 'user',
                parts: content.parts.map((part) => {
                    if ('text' in part && typeof part.text === 'string') {
                        return { ...part, text: InputSanitizer.sanitize(part.text) };
                    }
                    return part;
                })
            })) as Content[];
        }

        return prompt;
    }

    /**
     * Get circuit state for monitoring (internal/admin use)
     */
    public getCircuitStates(): Record<string, string> {
        return {
            content: this.contentBreaker.getState(),
            media: this.mediaBreaker.getState(),
            aux: this.auxBreaker.getState()
        };
    }

    private static instance: FirebaseIntelligenceService;
    public static getInstance(): FirebaseIntelligenceService {
        if (!FirebaseIntelligenceService.instance) {
            FirebaseIntelligenceService.instance = new FirebaseIntelligenceService();
        }
        return FirebaseIntelligenceService.instance;
    }
}

export const firebaseAI = FirebaseIntelligenceService.getInstance();
